import { useEffect } from 'react';
import { DraggableId, FluidDragActions, Position, Sensor, SensorAPI } from 'react-beautiful-dnd';

/** How close to the board's left or right edge, in px, the pointer has to be to scroll it. */
export const EDGE_ZONE = 60;
/** How long the pointer rests at the edge before each scroll, in ms. */
export const EDGE_DWELL = 500;

export interface ScrollBox {
  left: number;
  right: number;
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

/**
 * How far to scroll the board for a pointer at x: half the visible width towards the edge the pointer is at, as far as
 * the board goes, or 0 when the pointer is not at an edge or the board cannot scroll that way.
 */
export const edgeScrollDelta = (x: number, box: ScrollBox): number => {
  const step = Math.round(box.clientWidth / 2);
  const maxScrollLeft = box.scrollWidth - box.clientWidth;
  if (x >= box.right - EDGE_ZONE && box.scrollLeft < maxScrollLeft) return Math.min(step, maxScrollLeft - box.scrollLeft);
  if (x <= box.left + EDGE_ZONE && box.scrollLeft > 0) return -Math.min(step, box.scrollLeft);
  return 0;
};

/**
 * The element that scrolls the board sideways: the board's own box, or the nearest box above it that scrolls. The
 * ticket board's box grows to fit its lanes, and the page around it does the scrolling.
 */
export const closestSidewaysScroller = (from: HTMLElement | null): HTMLElement | null => {
  for (let element = from; element; element = element.parentElement) {
    if (element.scrollWidth > element.clientWidth && /auto|scroll/.test(getComputedStyle(element).overflowX)) return element;
  }
  return null;
};

/** The edges of what the user can see of the board: its box, cut to the window. */
const scrollBoxOf = (scroller: HTMLElement): ScrollBox => {
  const rect = scroller.getBoundingClientRect();
  return {
    left: Math.max(rect.left, 0),
    right: Math.min(rect.right, window.innerWidth),
    scrollLeft: scroller.scrollLeft,
    scrollWidth: scroller.scrollWidth,
    clientWidth: scroller.clientWidth,
  };
};

const handleCentre = (draggableId: DraggableId): Position | null => {
  const handle = document.querySelector(`[data-rbd-drag-handle-draggable-id="${CSS.escape(draggableId)}"]`);
  if (!handle) return null;
  const rect = handle.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

export interface FrameScheduler {
  request: (callback: FrameRequestCallback) => number;
  cancel: (frame: number) => void;
}

const browserFrames: FrameScheduler = {
  request: callback => requestAnimationFrame(callback),
  cancel: frame => cancelAnimationFrame(frame),
};

export interface EdgeScrollController {
  onMouseDown: (event: MouseEvent) => void;
  onMouseMove: (event: MouseEvent) => void;
  onMouseUp: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
  dispose: () => void;
}

/**
 * Scrolls the board sideways while a card is dragged at its left or right edge (#29099).
 *
 * The library auto-scrolls only the window and each lane's own list. The board's sideways scroll is a box above the
 * lanes, which it does not support, and it measures where every lane is once, as the drag starts - so scrolling the board
 * mid-drag left it aiming at the lane that used to be under the pointer, and the card dropped in the wrong one. Instead,
 * at the edge the drag is ended on the spot (no drop, no animation), the board scrolled, and the same card lifted again,
 * which measures the lanes afresh. It is lifted from where it sits and moved straight back under the pointer, so it does
 * not appear to move.
 *
 * Once it has re-lifted a card this carries the drag - the library's mouse sensor let go of it - so it follows the
 * pointer, drops on mouseup and cancels on Escape, as the library's would.
 *
 * The edge is watched a frame at a time only while a card is pressed, from mousedown to mouseup: with nothing pressed
 * there is no drag to scroll for, and a frame loop left running would cost every page the board is on.
 */
export const createEdgeScrollController = (
  api: SensorAPI,
  getScroller: () => HTMLElement | null,
  frames: FrameScheduler = browserFrames
): EdgeScrollController => {
  let pointer: Position | null = null;
  let pressedId: DraggableId | null = null;
  let carried: FluidDragActions | null = null;
  let atEdgeSince = 0;
  let frame = 0;

  const release = () => { carried = null; };

  const stopWatching = () => {
    if (frame) frames.cancel(frame);
    frame = 0;
    atEdgeSince = 0;
  };

  const relift = (draggableId: DraggableId, scroller: HTMLElement, delta: number, at: Position) => {
    api.tryReleaseLock();
    scroller.scrollLeft += delta;
    const home = handleCentre(draggableId);
    const preDrag = home && api.tryGetLock(draggableId, release);
    if (!preDrag) return;
    try {
      carried = preDrag.fluidLift(home);
      carried.move(at);
    } catch (error) {
      // The board allows one drag at a time; a lock kept by a lift that failed would refuse every drag after it.
      carried = null;
      api.tryReleaseLock();
      throw error;
    }
  };

  const tick = (now: number) => {
    frame = 0;
    if (!pressedId) return;
    frame = frames.request(tick);
    const scroller = getScroller();
    if (!api.isLockClaimed() || !pointer || !scroller) {
      atEdgeSince = 0;
      return;
    }
    const delta = edgeScrollDelta(pointer.x, scrollBoxOf(scroller));
    if (delta === 0) atEdgeSince = 0;
    else if (atEdgeSince === 0) atEdgeSince = now;
    else if (now - atEdgeSince >= EDGE_DWELL) {
      atEdgeSince = now;
      relift(pressedId, scroller, delta, pointer);
    }
  };

  return {
    onMouseDown: (event: MouseEvent) => {
      if (event.button !== 0) return;
      pressedId = api.findClosestDraggableId(event);
      if (pressedId && !frame) frame = frames.request(tick);
    },
    onMouseMove: (event: MouseEvent) => {
      pointer = { x: event.clientX, y: event.clientY };
      if (carried?.isActive()) carried.move(pointer);
    },
    onMouseUp: () => {
      if (carried?.isActive()) carried.drop();
      carried = null;
      pressedId = null;
      stopWatching();
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !carried?.isActive()) return;
      event.preventDefault();
      carried.cancel();
      carried = null;
    },
    dispose: () => {
      stopWatching();
      if (carried?.isActive()) carried.cancel();
      carried = null;
      pressedId = null;
    },
  };
};

/** The react-beautiful-dnd sensor for {@link createEdgeScrollController}. */
export const createEdgeScrollSensor = (getScroller: () => HTMLElement | null): Sensor =>
  function useEdgeScrollSensor(api: SensorAPI) {
    useEffect(() => {
      const controller = createEdgeScrollController(api, getScroller);

      window.addEventListener('mousedown', controller.onMouseDown, true);
      window.addEventListener('mousemove', controller.onMouseMove, true);
      window.addEventListener('mouseup', controller.onMouseUp, true);
      window.addEventListener('keydown', controller.onKeyDown, true);

      return () => {
        window.removeEventListener('mousedown', controller.onMouseDown, true);
        window.removeEventListener('mousemove', controller.onMouseMove, true);
        window.removeEventListener('mouseup', controller.onMouseUp, true);
        window.removeEventListener('keydown', controller.onKeyDown, true);
        controller.dispose();
      };
    }, [api]);
  };
